/**
 * Combines several zip archives into one, without recompressing anything.
 *
 * A large selection of certificates is downloaded in chunks, because one request for all of
 * them would outlast the platform's response timeout. The agent asked for one file, though,
 * so the chunks are stitched back together here.
 *
 * Each entry's compressed bytes are copied across untouched and only the headers around them
 * are rebuilt, so this costs a copy rather than a decompress-recompress. The local header is
 * written fresh from the central directory record, which is the authoritative copy of the
 * sizes and the CRC, so an archive that streamed its entries with data descriptors is
 * normalised on the way through.
 */

const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const EOCD_SIG = 0x06054b50;
const EOCD_MIN = 22;
const CENTRAL_MIN = 46;
const LOCAL_MIN = 30;

/** Set when an entry's real sizes follow the data instead of preceding it. */
const FLAG_DATA_DESCRIPTOR = 0x0008;
/** Set when the entry name is UTF-8 rather than CP437. We always write UTF-8. */
const FLAG_UTF8 = 0x0800;

/** A value of all ones in a size or count field means the real one lives in a Zip64 record. */
const ZIP64_32 = 0xffffffff;
const ZIP64_16 = 0xffff;

interface Entry {
  name: string;
  flags: number;
  method: number;
  modTime: number;
  modDate: number;
  crc32: number;
  compressedSize: number;
  uncompressedSize: number;
  externalAttrs: number;
  /** The entry's compressed bytes, exactly as they sat in the source archive. */
  data: Uint8Array;
}

/** Byte offset of the end of central directory record, or -1 when there is not one. */
function findEocd(view: DataView, length: number): number {
  // The record sits at the end, behind a comment of at most 65535 bytes.
  const earliest = Math.max(0, length - (EOCD_MIN + 0xffff));
  for (let i = length - EOCD_MIN; i >= earliest; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) return i;
  }
  return -1;
}

function readEntries(buffer: ArrayBuffer): Entry[] {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);

  const eocd = findEocd(view, bytes.length);
  if (eocd < 0) throw new Error('Not a zip archive: no end of central directory record');
  const count = view.getUint16(eocd + 10, true);
  const centralOffset = view.getUint32(eocd + 16, true);

  if (count === ZIP64_16 || centralOffset === ZIP64_32) {
    throw new Error('Zip64 archives are not supported');
  }

  const entries: Entry[] = [];
  let p = centralOffset;

  for (let i = 0; i < count; i++) {
    if (view.getUint32(p, true) !== CENTRAL_SIG) {
      throw new Error(`Malformed zip: expected a central directory record at byte ${p}`);
    }

    const flags = view.getUint16(p + 8, true);
    const compressedSize = view.getUint32(p + 20, true);
    const uncompressedSize = view.getUint32(p + 24, true);
    const nameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localOffset = view.getUint32(p + 42, true);

    if (compressedSize === ZIP64_32 || uncompressedSize === ZIP64_32 || localOffset === ZIP64_32) {
      throw new Error('Zip64 entries are not supported');
    }
    if (view.getUint32(localOffset, true) !== LOCAL_SIG) {
      throw new Error(`Malformed zip: no local header at byte ${localOffset}`);
    }

    // The local header's own name and extra lengths, which need not match the central
    // directory's, decide where this entry's data actually begins.
    const localNameLen = view.getUint16(localOffset + 26, true);
    const localExtraLen = view.getUint16(localOffset + 28, true);
    const dataStart = localOffset + LOCAL_MIN + localNameLen + localExtraLen;

    if (dataStart + compressedSize > bytes.length) {
      throw new Error('Malformed zip: entry data runs past the end of the archive');
    }

    entries.push({
      name: new TextDecoder().decode(bytes.subarray(p + CENTRAL_MIN, p + CENTRAL_MIN + nameLen)),
      flags,
      method: view.getUint16(p + 10, true),
      modTime: view.getUint16(p + 12, true),
      modDate: view.getUint16(p + 14, true),
      crc32: view.getUint32(p + 16, true),
      compressedSize,
      uncompressedSize,
      externalAttrs: view.getUint32(p + 38, true),
      data: bytes.subarray(dataStart, dataStart + compressedSize),
    });

    p += CENTRAL_MIN + nameLen + extraLen + commentLen;
  }

  return entries;
}

/**
 * Makes `name` unique against the names already used, the way a file manager would: the
 * second "Acme Holdings.pdf" becomes "Acme Holdings (2).pdf".
 *
 * Needed because each chunk is named independently by the API and knows nothing of the
 * others, so a certificate holder appearing in two chunks arrives twice under one name.
 */
function uniqueName(name: string, used: Map<string, number>): string {
  const seen = used.get(name);
  if (seen === undefined) {
    used.set(name, 1);
    return name;
  }

  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';

  let n = seen;
  let candidate: string;
  do {
    n += 1;
    candidate = `${stem} (${n})${ext}`;
  } while (used.has(candidate));

  used.set(name, n);
  used.set(candidate, 1);
  return candidate;
}

/**
 * Stitches `buffers` into a single archive, in the order given.
 *
 * Returns the bytes of the combined zip. Throws if a buffer is not a readable zip, so the
 * caller can say that rather than hand the agent something that will not open.
 */
export function mergeZips(buffers: ArrayBuffer[]): Uint8Array {
  const encoder = new TextEncoder();
  const used = new Map<string, number>();

  const staged = buffers
    .flatMap(readEntries)
    .map(entry => {
      const name = encoder.encode(uniqueName(entry.name, used));
      return { entry, name };
    });

  const total = staged.reduce(
    (sum, s) => sum + LOCAL_MIN + s.name.length + s.entry.data.length + CENTRAL_MIN + s.name.length,
    EOCD_MIN,
  );

  const out = new Uint8Array(total);
  const view = new DataView(out.buffer);
  let at = 0;

  const offsets: number[] = [];

  for (const { entry, name } of staged) {
    offsets.push(at);

    // Written from the central directory's values, so the sizes and CRC are present here even
    // if the source archive had deferred them to a data descriptor. That bit is cleared to
    // match, and the UTF-8 bit set because the name is encoded as UTF-8 above.
    view.setUint32(at, LOCAL_SIG, true);
    view.setUint16(at + 4, 20, true);
    view.setUint16(at + 6, (entry.flags & ~FLAG_DATA_DESCRIPTOR) | FLAG_UTF8, true);
    view.setUint16(at + 8, entry.method, true);
    view.setUint16(at + 10, entry.modTime, true);
    view.setUint16(at + 12, entry.modDate, true);
    view.setUint32(at + 14, entry.crc32, true);
    view.setUint32(at + 18, entry.compressedSize, true);
    view.setUint32(at + 22, entry.uncompressedSize, true);
    view.setUint16(at + 26, name.length, true);
    view.setUint16(at + 28, 0, true);
    at += LOCAL_MIN;

    out.set(name, at);
    at += name.length;
    out.set(entry.data, at);
    at += entry.data.length;
  }

  const centralStart = at;

  staged.forEach(({ entry, name }, i) => {
    view.setUint32(at, CENTRAL_SIG, true);
    view.setUint16(at + 4, 20, true);
    view.setUint16(at + 6, 20, true);
    view.setUint16(at + 8, (entry.flags & ~FLAG_DATA_DESCRIPTOR) | FLAG_UTF8, true);
    view.setUint16(at + 10, entry.method, true);
    view.setUint16(at + 12, entry.modTime, true);
    view.setUint16(at + 14, entry.modDate, true);
    view.setUint32(at + 16, entry.crc32, true);
    view.setUint32(at + 20, entry.compressedSize, true);
    view.setUint32(at + 24, entry.uncompressedSize, true);
    view.setUint16(at + 28, name.length, true);
    view.setUint16(at + 30, 0, true);
    view.setUint16(at + 32, 0, true);
    view.setUint16(at + 34, 0, true);
    view.setUint16(at + 36, 0, true);
    view.setUint32(at + 38, entry.externalAttrs, true);
    view.setUint32(at + 42, offsets[i], true);
    at += CENTRAL_MIN;

    out.set(name, at);
    at += name.length;
  });

  view.setUint32(at, EOCD_SIG, true);
  view.setUint16(at + 4, 0, true);
  view.setUint16(at + 6, 0, true);
  view.setUint16(at + 8, staged.length, true);
  view.setUint16(at + 10, staged.length, true);
  view.setUint32(at + 12, at - centralStart, true);
  view.setUint32(at + 16, centralStart, true);
  view.setUint16(at + 20, 0, true);

  return out;
}

/** Zip's method 8 is raw deflate, with no zlib wrapper around it. */
const METHOD_DEFLATE = 8;
/** Method 0 is stored: the bytes sitting in the archive are already the file. */
const METHOD_STORED = 0;

/**
 * Pulls the only file out of a single-entry archive, so that asking for one certificate hands
 * back that certificate rather than a folder containing it.
 *
 * Returns null whenever the archive is not a single entry this can safely unpack, including on
 * a browser with no DecompressionStream, so the caller can fall back to saving the archive. An
 * unexpected .zip is a smaller problem than no download at all.
 */
export async function readSingleEntry(
  buffer: ArrayBuffer,
): Promise<{ name: string; bytes: Uint8Array } | null> {
  try {
    const bytes = new Uint8Array(buffer);
    const view = new DataView(buffer);

    const eocd = findEocd(view, bytes.length);
    if (eocd < 0 || view.getUint16(eocd + 10, true) !== 1) return null;

    const p = view.getUint32(eocd + 16, true);
    if (view.getUint32(p, true) !== CENTRAL_SIG) return null;

    const method = view.getUint16(p + 10, true);
    const compressedSize = view.getUint32(p + 20, true);
    const nameLen = view.getUint16(p + 28, true);
    const localOffset = view.getUint32(p + 42, true);
    if (compressedSize === ZIP64_32 || localOffset === ZIP64_32) return null;

    const name = new TextDecoder().decode(bytes.subarray(p + CENTRAL_MIN, p + CENTRAL_MIN + nameLen));

    // The local header's own name and extra lengths, which need not match the central
    // directory's, decide where this entry's data begins.
    const start =
      localOffset + LOCAL_MIN + view.getUint16(localOffset + 26, true) + view.getUint16(localOffset + 28, true);
    const raw = bytes.subarray(start, start + compressedSize);

    if (method === METHOD_STORED) return { name, bytes: raw };
    if (method !== METHOD_DEFLATE) return null;

    const Decompressor = (window as any).DecompressionStream;
    if (!Decompressor) return null;

    const stream = new Blob([raw]).stream().pipeThrough(new Decompressor('deflate-raw'));
    return { name, bytes: new Uint8Array(await new Response(stream).arrayBuffer()) };
  } catch {
    return null;
  }
}

/**
 * Number of files in a zip, read from its end of central directory record.
 *
 * download-zip drops any certificate it could not fetch and returns an archive of whatever
 * succeeded, so counting what actually arrived is the only way to tell the agent they got
 * fewer than they asked for. Returns null if the record cannot be found, in which case the
 * caller should say nothing rather than guess.
 */
export function countZipEntries(buffer: ArrayBuffer): number | null {
  const view = new DataView(buffer);
  const at = findEocd(view, buffer.byteLength);
  return at < 0 ? null : view.getUint16(at + 10, true);
}
